/**
 * Shared gate for the inline preview editor.
 *
 * Edit mode is available iff:
 *
 *   * Tenant shell — user is authenticated AND has an ``admin``
 *     membership in the tenant currently resolved by the public
 *     branding endpoint (slug match).
 *   * Platform shell (``forgmatic.com``) — user is authenticated AND
 *     ``is_platform_admin === true`` on their session. The
 *     ``.forgmatic.com`` cookie domain carries the same session from
 *     ``admin.forgmatic.com``, so platform staff who logged in to the
 *     admin shell can hop to the marketing landing and edit there
 *     without a second sign-in.
 *
 * Platform-admin shell (``admin.forgmatic.com``) itself never enters
 * edit mode — its surface is the staff console, not a render target.
 *
 * Returns both the gate (``canEdit``) and whether the URL has actually
 * activated the editor (``isActive``). Two consumers share this:
 *   - ``EditModeBootstrap`` toggles ``editModeStore`` + renders the bar
 *     + overlay when ``isActive``.
 *   - ``EditModeFAB`` shows a "Upravit stránku" button when ``canEdit``
 *     is true but the editor is not yet active.
 */

import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { useAuthMe } from "@/api/queries/auth";
import { useTenantBranding } from "@/api/queries/branding";
import { resolveShell } from "@/shell/resolveShell";

export interface EditModeAccess {
  canEdit: boolean;
  isActive: boolean;
}

export function useEditModeAccess(): EditModeAccess {
  const shell = resolveShell();
  const isTenantShell = shell === "tenant";
  const isPlatformShell = shell === "platform";
  const isEditableShell = isTenantShell || isPlatformShell;

  const auth = useAuthMe({ enabled: isEditableShell });
  // Tenant shell needs the branding payload to know which tenant the
  // request resolved to (slug match against the user's memberships).
  // Platform shell skips it — gate is purely role-based.
  const branding = useTenantBranding({ enabled: isTenantShell });

  const canEdit = useMemo(() => {
    if (!isEditableShell || !auth.data) return false;
    if (isPlatformShell) {
      return auth.data.is_platform_admin === true;
    }
    if (!branding.data) return false;
    const slug = branding.data.slug;
    return (auth.data.org_memberships ?? []).some(
      (m) => m.slug === slug && m.role === "admin",
    );
  }, [isEditableShell, isPlatformShell, auth.data, branding.data]);

  const location = useLocation();
  const requested = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("_edit") === "1";
  }, [location.search]);

  return { canEdit, isActive: canEdit && requested };
}
